import React, { useEffect, useState } from 'react';
import {
    Button,
    Chip,
    createStyles,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    makeStyles,
    TextField,
    Theme,
    Typography,
} from '@material-ui/core';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import LinkOffIcon from '@material-ui/icons/LinkOff';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import SyncIcon from '@material-ui/icons/Sync';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { CalendlyLogo } from '../../v2/common/svg';
import { SkeletonLoader } from '../../components/loading/SkeletonLoader';
import { showNotification } from '../../App';
import { client } from '../..';
import { FetchPolicy } from 'apollo-client';
import gql from 'graphql-tag';
import { SourceSelector } from '../../enquirySummary/selectors/SourceSelector';
import { AreaOfLawSelector, IAreaOfLawAutoCompleteItem } from '../../enquirySummary/selectors/AreaOfLawSelector';
import { IAutoCompleteItem } from '../../typings/autoComplete';
import { FormSectionAccordionCard } from '../../components/cards/FormSectionAccordionCard';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: 'flex',
            flex: 1,
            flexFlow: 'column',
            padding: 30,
        },
        disconnectedWrap: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: theme.spacing(6, 4),
            textAlign: 'center',
        },
        calendlyIconWrap: {
            width: 64,
            height: 64,
            borderRadius: 16,
            backgroundColor: '#e8f0fe',
            color: '#0b5fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: theme.spacing(2),
        },
        disconnectedTitle: {
            fontWeight: 600,
            marginBottom: theme.spacing(1),
        },
        disconnectedSubtitle: {
            color: theme.palette.text.secondary,
            maxWidth: 420,
            marginBottom: theme.spacing(3),
        },
        connectedHeader: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1.5),
            marginBottom: theme.spacing(3),
        },
        connectedBadge: {
            backgroundColor: '#e8f5e9',
            color: '#2e7d32',
            fontWeight: 600,
            fontSize: 12,
        },
        connectedTitle: {
            fontWeight: 600,
            flex: 1,
        },
        detailRow: {
            marginBottom: theme.spacing(2),
        },
        detailLabel: {
            fontSize: 11,
            fontWeight: 600,
            textTransform: 'uppercase' as const,
            letterSpacing: '0.06em',
            color: theme.palette.text.disabled,
            marginBottom: theme.spacing(0.5),
        },
        detailValue: {
            fontSize: 13,
            color: theme.palette.text.primary,
            wordBreak: 'break-all' as const,
        },
        divider: {
            margin: theme.spacing(2, 0),
        },
        connectedActions: {
            display: 'flex',
            gap: theme.spacing(1.5),
        },
        disconnectButton: {
            color: theme.palette.error.main,
            borderColor: theme.palette.error.main,
            '&:hover': {
                backgroundColor: '#fff0f0',
                borderColor: theme.palette.error.dark,
            },
        },
        dialogIcon: {
            width: 48,
            height: 48,
            borderRadius: 12,
            backgroundColor: '#e8f0fe',
            color: '#0b5fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: theme.spacing(2),
        },
        dialogBody: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            padding: theme.spacing(1, 0),
        },
        dialogSubtitle: {
            color: theme.palette.text.secondary,
            marginBottom: theme.spacing(2),
        },
        bulletList: {
            textAlign: 'left' as const,
            marginTop: theme.spacing(1),
            paddingLeft: theme.spacing(2.5),
            color: theme.palette.text.secondary,
            fontSize: 13,
            '& li': {
                marginBottom: theme.spacing(0.5),
            },
        },
        // Offset the dialog container so it centres within the main content area
        // rather than the full viewport (which includes the 260px sidebar).
        dialogContainer: {
            paddingLeft: 260,
        },
        textField: {
            width: '100%',
        },
        actionsRow: {
            display: 'flex',
            justifyContent: 'flex-end',
            gap: theme.spacing(1.5),
            marginTop: theme.spacing(2),
        },
        calendlyQuestionGrid: {
            paddingTop: '20px'
        }
    })
);

interface CalendlyTabState {
    isConnected: boolean;
    organizationUri: string | null;
    userUri: string | null;
    authorizeUrl: string | null;
    isDialogOpen: boolean;
    isLoading: boolean;

    // Calendly Question states
    source: string | null;
    areaOfLaw: string | null;
    areaOfLawQuestion: string | null;
    isSavingQuestions: boolean;
    savedSource: string | null;
    savedAreaOfLaw: string | null;
    savedAreaOfLawQuestion: string | null;
}

export default function CalendlyTab() {

    const classes = useStyles();

    const [state, setState] = useState<CalendlyTabState>({
        isConnected: false,
        organizationUri: null,
        userUri: null,
        authorizeUrl: null,
        isDialogOpen: false,
        isLoading: true,

        source: null,
        areaOfLaw: null,
        areaOfLawQuestion: null,
        isSavingQuestions: false,
        savedSource: null,
        savedAreaOfLaw: null,
        savedAreaOfLawQuestion: null
    });

    useEffect(() => {
        fetchData();
    // tslint:disable-next-line: align
    }, []);

    const fetchData = () => {
        retrieveCalendlyConfigQueryData(
            true, // this.state.isRefresh, -- Since we are updating and creating, we do not fetch cached data
            // tslint:disable-next-line: no-console
            (data) => onRetrieveData(data),
            // tslint:disable-next-line:no-any
            function (reason: any): void {
                showNotification(null, reason, 'error');
                setState((prevState) => {
                    return {
                        ...prevState,
                        isLoading: false
                    };
                });
            }
        );

        retrieveCalendlyQuestionsData (
            true,
            (data) => onRetrieveQuestionData(data),
            function (reason: any): void {
                showNotification(null, reason, 'error');
                setState((prevState) => {
                    return {
                        ...prevState,
                        isLoading: false
                    };
                });
            }
        )
    };

    const onRetrieveData = (data: CalendlyConfigData) => {
        const config = data.settings.integrations.calendly.config;
        setState((prevState) => {
            return {
                ...prevState,
                isConnected: config.isActive,
                organizationUri: config.calendlyOrganizationUri ?? null,
                userUri: config.calendlyUserUri ?? null,
                authorizeUrl: config.authorizeUrl ?? null,
                isLoading: false
            };
        });
    };

    const onRetrieveQuestionData = (data: CalendltQuestionsConfigData) => {
        const config = data.settings.applications.calendlyQuestion.config;
        setState((prevState) => {
          return {
              ...prevState,
            source: config.source,
            areaOfLaw: config.areaOfLaw,
            areaOfLawQuestion: config.areaOfLawQuestion,
            savedSource: config.source,
            savedAreaOfLaw: config.areaOfLaw,
            savedAreaOfLawQuestion: config.areaOfLawQuestion
          }
        });
    }

    const onOpenCalendly = () => {
        // Full-tab redirect — Calendly redirects back to /calendly/auth with ?code&state,
        // which completes the connection and redirects back here.
        if (state.authorizeUrl) {
            window.location.href = state.authorizeUrl;
            return;
        }

        // Re-fetch (rather than firing a one-off separate query) if our own state hasn't
        // picked up authorizeUrl yet for some reason.
        retrieveCalendlyConfigQueryData(
            true,
            (data) => {
                const freshAuthorizeUrl = data.settings.integrations.calendly.config.authorizeUrl;
                if (freshAuthorizeUrl) {
                    window.location.href = freshAuthorizeUrl;
                } else {
                    showNotification('Failed to open Calendly', 'Authorize URL is not available. Please try again.', 'error');
                }
            },
            // tslint:disable-next-line: no-any
            function (reason: any): void {
                showNotification('Failed to open Calendly', reason, 'error');
            }
        );
    };

    const onDisconnect = () => {
        client
            .mutate({
                mutation: DisconnectCalendlyMutation,
            })
            // tslint:disable-next-line: no-any
            .then((results: { data: any }) => {
                if (results.data) {
                    if (results.data.integrations.calendly.disconnect.status && results.data.integrations.calendly.disconnect.error === null) {
                        setState((prevState) => {
                            return {
                                ...prevState,
                                isConnected: false,
                                organizationUri: null,
                                userUri: null,
                            };
                        });
                        showNotification(null, 'Calendly disconnected', 'info');
                    } else {
                        showNotification('Failed to disconnect Calendly', results.data.integrations.calendly.disconnect.error, 'error');
                    }
                }
            })
            // tslint:disable-next-line: no-any
            .catch((reason: any) => {
                showNotification('Failed to disconnect Calendly', reason, 'error');
            });
    };

    const onSaveCalendlyQuestion = () => {
        setState((prevState) => {
            return {
                ...prevState,
                isSavingQuestions: true
            };
        });

        client
            .mutate({
                mutation: UpdateCalendlyQuestionMutation,
                variables: {
                    source: state.source,
                    areaOfLaw: state.areaOfLaw,
                    areaOfLawQuestion: state.areaOfLawQuestion,
                },
            })
            // tslint:disable-next-line: no-any
            .then((results: { data: any }) => {
                setState((prevState) => {
                    return {
                        ...prevState,
                        isSavingQuestions: false
                    };
                });

                if (results.data) {
                    const response = results.data.applicationSettings.calendlySettings.updateQuestions;
                    if (response.status && response.error === null) {
                        setState((prevState) => {
                            return {
                                ...prevState,
                                savedSource: prevState.source,
                                savedAreaOfLaw: prevState.areaOfLaw,
                                savedAreaOfLawQuestion: prevState.areaOfLawQuestion
                            };
                        });
                        showNotification(null, 'Successfully submitted.', 'info');
                    } else {
                        showNotification('Failed to update Calendly questions', response.error, 'error');
                    }
                }
            })
            // tslint:disable-next-line: no-any
            .catch((reason: any) => {
                setState((prevState) => {
                    return {
                        ...prevState,
                        isSavingQuestions: false
                    };
                });
                showNotification('Failed to update Calendly questions', reason, 'error');
            });
    };

    const onResetCalendlyQuestion = () => {
        setState((prevState) => {
            return {
                ...prevState,
                source: null,
                areaOfLaw: null,
                areaOfLawQuestion: null
            };
        });
    };

    const isSourceTouched = state.source !== state.savedSource;
    const isAreaOfLawTouched = state.areaOfLaw !== state.savedAreaOfLaw;
    const isAreaOfLawQuestionTouched = state.areaOfLawQuestion !== state.savedAreaOfLawQuestion;
    const isQuestionFormPristine = !isSourceTouched && !isAreaOfLawTouched && !isAreaOfLawQuestionTouched;

    const isAreaOfLawRequired = !state.areaOfLawQuestion;

    const isSaveQuestionDisabled = state.isSavingQuestions || isQuestionFormPristine || !state.source
        || (isAreaOfLawRequired && !state.areaOfLaw);

    if (state.isLoading) {
        return (
            <div className={classes.root}>
                <SkeletonLoader variant="form" rows={3} columns={1} />
            </div>
        );
    }

    return (
        <div className={classes.root}>

            {state.isConnected ? (
                <Grid container={true} spacing={3}>
                    <Grid item={true} xs={12}>
                        <div className={classes.connectedHeader}>
                            <div className={classes.calendlyIconWrap} style={{ width: 40, height: 40 }}>
                                <CalendlyLogo size={24} />
                            </div>
                            <Typography variant="h6" className={classes.connectedTitle}>
                                Calendly
                            </Typography>
                            <Chip
                                label="Connected"
                                size="small"
                                icon={<CheckCircleOutlineIcon style={{ fontSize: 14 }} />}
                                className={classes.connectedBadge}
                            />
                        </div>
                    </Grid>

                    {state.organizationUri && (
                        <Grid item={true} xs={12} md={8}>
                            <div className={classes.detailRow}>
                                <div className={classes.detailLabel}>Organization URI</div>
                                <div className={classes.detailValue}>{state.organizationUri}</div>
                            </div>
                        </Grid>
                    )}

                    {state.userUri && (
                        <Grid item={true} xs={12} md={8}>
                            <div className={classes.detailRow}>
                                <div className={classes.detailLabel}>User URI</div>
                                <div className={classes.detailValue}>{state.userUri}</div>
                            </div>
                        </Grid>
                    )}

                    <Grid item={true} xs={12}>
                        <Divider className={classes.divider} />
                        <div className={classes.connectedActions}>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<SyncIcon />}
                                onClick={() => setState(prev => ({ ...prev, isDialogOpen: true }))}
                            >
                                Reconnect Calendly
                            </Button>
                            <Button
                                variant="outlined"
                                className={classes.disconnectButton}
                                startIcon={<LinkOffIcon />}
                                onClick={onDisconnect}
                            >
                                Disconnect Calendly
                            </Button>
                        </div>
                    </Grid>
                </Grid>
            ) : (
                <div className={classes.disconnectedWrap}>
                    <div className={classes.calendlyIconWrap}>
                        <CalendlyLogo size={36} />
                    </div>
                    <Typography variant="h6" className={classes.disconnectedTitle}>
                        Connect to Calendly
                    </Typography>
                    <Typography variant="body2" className={classes.disconnectedSubtitle}>
                        Link your Calendly account so your bookings automatically sync with RedView.
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<CalendlyLogo size={18} />}
                        onClick={() => setState(prev => ({ ...prev, isDialogOpen: true }))}
                    >
                        Connect Calendly
                    </Button>
                </div>
            )}

            <Grid item={true} xs={12} className={classes.calendlyQuestionGrid}>
                <FormSectionAccordionCard
                    title="Calendly Questions"
                    icon={HelpOutlineIcon}
                    color="#0b5fff"
                    bgColor="#e8f0fe"
                >
                    <Grid container={true} spacing={3}>
                        <Grid item={true} xs={12} md={6}>
                            <SourceSelector
                                label="Source"
                                name="source"
                                value={state.source ? { value: state.source, label: state.source } : null}
                                required={true}
                                className={classes.textField}
                                onSelection={(selection: IAutoCompleteItem) => {
                                    setState((prevState) => {
                                        return {
                                            ...prevState,
                                            source: selection ? selection.label : null
                                        };
                                    });
                                }}
                            />
                        </Grid>
                        <Grid item={true} xs={12} md={6}>
                            <AreaOfLawSelector
                                label="Area of Law"
                                name="areaOfLaw"
                                value={state.areaOfLaw ? { value: state.areaOfLaw, label: state.areaOfLaw } : null}
                                required={isAreaOfLawRequired}
                                className={classes.textField}
                                onSelection={(selection: IAreaOfLawAutoCompleteItem) => {
                                    setState((prevState) => {
                                        return {
                                            ...prevState,
                                            areaOfLaw: selection ? selection.label : null
                                        };
                                    });
                                }}
                            />
                        </Grid>
                        <Grid item={true} xs={12}>
                            <TextField
                                label="Area of Law Question"
                                name="areaOfLawQuestion"
                                value={state.areaOfLawQuestion ?? ''}
                                className={classes.textField}
                                onChange={(event) => {
                                    const areaOfLawQuestion = event.target.value;
                                    setState((prevState) => {
                                        return {
                                            ...prevState,
                                            areaOfLawQuestion
                                        };
                                    });
                                }}
                            />
                        </Grid>
                    </Grid>
                    <div className={classes.actionsRow}>
                        <Button
                            variant="outlined"
                            color="primary"
                            disabled={isSaveQuestionDisabled}
                            onClick={onSaveCalendlyQuestion}
                        >
                            Save
                        </Button>
                        <Button
                            variant="text"
                            color="default"
                            disabled={state.isSavingQuestions}
                            onClick={onResetCalendlyQuestion}
                        >
                            Reset
                        </Button>
                    </div>
                </FormSectionAccordionCard>
            </Grid>

            {/* ── Connect Dialog ── */}
            <Dialog
                open={state.isDialogOpen}
                onClose={() => setState(prev => ({ ...prev, isDialogOpen: false }))}
                maxWidth="xs"
                fullWidth={true}
                classes={{ container: classes.dialogContainer }}
            >
                <DialogTitle>Connect to Calendly</DialogTitle>
                <DialogContent>
                    <div className={classes.dialogBody}>
                        <div className={classes.dialogIcon}>
                            <CalendlyLogo size={28} />
                        </div>
                        <Typography variant="body2" className={classes.dialogSubtitle}>
                            You will be redirected to Calendly to authorise RedView. We will request permission to:
                        </Typography>
                        <ul className={classes.bulletList}>
                            <li>Read your organisation and user details</li>
                            <li>Automatically receive your booking events</li>
                        </ul>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setState(prev => ({ ...prev, isDialogOpen: false }))}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<OpenInNewIcon />}
                        onClick={onOpenCalendly}
                    >
                        Open Calendly
                    </Button>
                </DialogActions>
            </Dialog>

        </div>
    );
}

export interface CalendlyConfig {
    id: string;
    authorizeUrl: string | null;
    isActive: boolean;
    accessTokenExpiresAt: string | null;
    scope: string | null;
    tokenType: string | null;
    calendlyUserUri: string | null;
    calendlyOrganizationUri: string | null;
    calendlyUserEmail: string | null;
    webhookSubscriptionId: string | null;
    connectedByUserId: string | null;
    connectedDate: string | null;
}

export interface CalendlyQuestionConfig {
    source: string;
    areaOfLaw: string;
    areaOfLawQuestion: string
}

export interface CalendlyConfigData {
    settings: {
        id: string;
        integrations: {
            id: string;
            calendly: {
                id: string;
                config: CalendlyConfig;
            };
        };
    };
}

export interface CalendltQuestionsConfigData {
    settings: {
        id: string;
        applications: {
            id: string;
            calendlyQuestion: {
                id: string;
                config: CalendlyQuestionConfig
            }
        }
    }
}

export const CalendlyConfigQuery = gql`
    query calendlyQuery {
        settings {
            id
            integrations {
                id
                calendly {
                    id
                    config {
                        id
                        authorizeUrl
                        isActive
                        accessTokenExpiresAt
                        scope
                        tokenType
                        calendlyUserUri
                        calendlyOrganizationUri
                        calendlyUserEmail
                        webhookSubscriptionId
                        connectedByUserId
                        connectedDate
                    }
                }
            }
        }
    }
`;

export const CalendlyQuestionsQuery = gql`
    query CalendlyQuestion {
        settings {
            id
            applications {
                id  
                calendlyQuestion {
                    id
                    config {
                        id
                        source
                        areaOfLaw
                        areaOfLawQuestion
                    }
                }
            }
        }
    }
`;

export function retrieveCalendlyQuestionsData (
    refreshData: boolean,
    onSuccess: (data: CalendltQuestionsConfigData) => void,
    onError: (reason: any) => void
) : void {
    var fetchPolicy: FetchPolicy = refreshData === true ? 'network-only' : 'cache-first';
    client
        .query({
            query: CalendlyQuestionsQuery,
            variables: {},
            fetchPolicy: fetchPolicy,
        })
        // tslint:disable-next-line:no-any
        .then((results: { data: any }) => {
            onSuccess(results.data);
        })
        // tslint:disable-next-line:no-any
        .catch((reason: any) => {
            onError(reason);
        });
}

export function retrieveCalendlyConfigQueryData(
    refreshData: boolean,
    onSuccess: (data: CalendlyConfigData) => void,
    // tslint:disable-next-line:no-any
    onError: (reason: any) => void
): void {
    var fetchPolicy: FetchPolicy = refreshData === true ? 'network-only' : 'cache-first';
    client
        .query({
            query: CalendlyConfigQuery,
            variables: {},
            fetchPolicy: fetchPolicy,
        })
        // tslint:disable-next-line:no-any
        .then((results: { data: any }) => {
            onSuccess(results.data);
        })
        // tslint:disable-next-line:no-any
        .catch((reason: any) => {
            onError(reason);
        });
}

const DisconnectCalendlyMutation = gql`
    mutation DisconnectCalendly {
        integrations {
            calendly {
                disconnect {
                    error
                    status
                }
            }
        }
    }
`;

const UpdateCalendlyQuestionMutation = gql`
    mutation UpdateCalendlyQuestions (
        $source: String,
        $areaOfLaw: String,
        $areaOfLawQuestion: String
    ) {
        applicationSettings {
            calendlySettings {
                updateQuestions (
                    source: $source,
                    areaOfLaw: $areaOfLaw,
                    areaOfLawQuestion: $areaOfLawQuestion
                ) {
                    error
                    status
                }
            }
        }
    }
`;
